import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
    fetchModelCapabilityMaps,
    fetchModelCapabilityMapById,
    fetchPhasesByCapabilityMapId,
    fetchItemsByPhaseId,
    createModelCapabilityMap,
    updateModelCapabilityMap,
    deleteModelCapabilityMap,
    toggleCapabilityMapVisibility,
    duplicateModelCapabilityMap,
    fetchIndustries
} from '../services/capabilityMapService';
import { toast } from 'react-toastify';

export const useFetchModelCapabilityMaps = (industry = 'all', page = 1, limit = 12, includeInactive = false) => {
    return useQuery({
        queryKey: ['capabilityMaps', { industry, page, limit, includeInactive }],
        queryFn: fetchModelCapabilityMaps,
        keepPreviousData: true,
        staleTime: 5 * 60 * 1000,
    });
};

export const useFetchModelCapabilityMapById = (id) => {
    return useQuery({
        queryKey: ['capabilityMap', id],
        queryFn: () => fetchModelCapabilityMapById(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000,
    });
};

export const useFetchPhasesByCapabilityMapId = (capabilityMapId) => {
    return useQuery({
        queryKey: ['phases', capabilityMapId],
        queryFn: () => fetchPhasesByCapabilityMapId(capabilityMapId),
        enabled: !!capabilityMapId,
        staleTime: 5 * 60 * 1000,
    });
};

export const useFetchItemsByPhaseId = (phaseId) => {
    return useQuery({
        queryKey: ['items', phaseId],
        queryFn: () => fetchItemsByPhaseId(phaseId),
        enabled: !!phaseId,
        staleTime: 5 * 60 * 1000,
    });
};

export const useFetchIndustries = () => {
    return useQuery({
        queryKey: ['industries'],
        queryFn: fetchIndustries,
        staleTime: 10 * 60 * 1000,
    });
};

export const useCreateModelCapabilityMap = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createModelCapabilityMap,
        onSuccess: (response) => {
            if (response?.success) {
                toast.success(response?.message || 'Capability map created successfully');
                queryClient.invalidateQueries({ queryKey: ['capabilityMaps'] });
            } else {
                toast.error(response?.message || 'Error creating capability map');
            }
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Error creating capability map');
        },
    });
};

export const useUpdateModelCapabilityMap = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }) => updateModelCapabilityMap(id, data),
        onSuccess: (response, variables) => {
            if (response?.success) {
                toast.success(response?.message || 'Capability map updated successfully');
                queryClient.invalidateQueries(['capabilityMaps']);
                queryClient.invalidateQueries(['capabilityMap', variables.id]);
            }
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Error updating capability map');
        },
    });
};

export const useDeleteModelCapabilityMap = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteModelCapabilityMap,
        onSuccess: (response) => {
            if (response?.success) {
                toast.success(response?.message || 'Capability map deleted successfully');
                queryClient.invalidateQueries(['capabilityMaps']);
            }
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Error deleting capability map');
        },
    });
};

export const useToggleCapabilityMapVisibility = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: toggleCapabilityMapVisibility,
        onSuccess: (response, variables) => {
            if (response?.success) {
                toast.success(response?.message || 'Visibility toggled successfully');
                queryClient.invalidateQueries(['capabilityMaps']);
                queryClient.invalidateQueries(['capabilityMap', variables]);
            }
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Error toggling visibility');
        },
    });
};

export const useDuplicateModelCapabilityMap = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, newName }) => duplicateModelCapabilityMap(id, newName),
        onSuccess: (response) => {
            if (response?.success) {
                toast.success(response?.message || 'Capability map duplicated successfully');
                queryClient.invalidateQueries(['capabilityMaps']);
            }
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Error duplicating capability map');
        },
    });
};